import React, { useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import useMutation from "../../../hooks/useMutation";
import useNotification from "../../../hooks/notification";
import {
  ADD_SQUARE_FOOTAGE_TYPE_MUTATION,
  UPDATE_SQUARE_FOOTAGE_TYPE_MUTATION,
} from "../../../constants/graphql";
import { FETCH_PROJECT_QUERY } from "../../../constants/graphql/queries";
import SquareFootageTypeModal from "../../../views/settings/Miscellaneous/SquareFootageTypeModal";
import ConfirmDeleteModal from "../../images/ConfirmDeleteModal";
import { useProject } from "../ProjectProvider";
import { getSortedSquareFootageTypes } from "../../../utils/squareFootageHelpers";

export default function ProjectSquareFootageTypesDialog({ onClose, open }) {
  const { project } = useProject();
  const { notifySuccess, notifyError } = useNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [addSquareFootageType] = useMutation(ADD_SQUARE_FOOTAGE_TYPE_MUTATION);
  const [updateSquareFootageType] = useMutation(UPDATE_SQUARE_FOOTAGE_TYPE_MUTATION);

  const squareFootageTypes = useMemo(
    () => getSortedSquareFootageTypes(project?.squareFootageTypes || []),
    [project?.squareFootageTypes]
  );

  const refetchProject = [{ query: FETCH_PROJECT_QUERY, variables: { id: project.id } }];

  const handleSave = name => {
    const existingInactive = squareFootageTypes.find(
      type => type.name?.toLowerCase() === name.toLowerCase() && !type.isActive
    );
    const mutation = existingInactive ? updateSquareFootageType : addSquareFootageType;
    const variables = existingInactive
      ? { projectId: project.id, squareFootageTypeId: existingInactive.id, isActive: true }
      : { projectId: project.id, name };

    mutation({
      variables,
      refetchQueries: refetchProject,
      onSuccess: () => {
        notifySuccess(
          existingInactive
            ? `Square footage type "${existingInactive.name}" restored successfully`
            : `Square footage type "${name}" added successfully`
        );
        setModalOpen(false);
      },
      onFailure: error => notifyError(error),
    });
  };

  const handleRemove = () => {
    const type = squareFootageTypes.find(entry => entry.id === deleteId);
    if (!type) return;

    updateSquareFootageType({
      variables: { projectId: project.id, squareFootageTypeId: type.id, isActive: false },
      refetchQueries: refetchProject,
      onSuccess: () => {
        notifySuccess(`Square footage type "${type.name}" removed successfully`);
        setDeleteId(null);
      },
      onFailure: error => {
        notifyError(error);
        setDeleteId(null);
      },
    });
  };

  const deletedType = squareFootageTypes.find(type => type.id === deleteId);

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { width: "600px", maxWidth: "90vw", display: "flex", flexDirection: "column" },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography color="textSecondary" fontSize={24} fontWeight={500} align="center">
            Edit Square Footage Types
          </Typography>
          <IconButton
            sx={{ position: "absolute", right: 8, top: 8 }}
            color="textPrimary"
            onClick={onClose}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{ flexGrow: 1, overflow: "hidden", display: "flex", flexDirection: "column", pt: 2 }}
        >
          <Box mb={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setModalOpen(true)}>
              Add Square Footage Type
            </Button>
          </Box>
          <Box sx={{ flexGrow: 1, overflow: "auto" }}>
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {squareFootageTypes
                    .filter(type => type.isActive)
                    .map(type => (
                      <TableRow key={type.id}>
                        <TableCell component="th" scope="row">
                          <Typography color="text.primary">{type.name}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => setDeleteId(type.id)}
                            title="Remove"
                            size="small"
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="outlined" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {modalOpen && (
        <SquareFootageTypeModal onClose={() => setModalOpen(false)} onSave={handleSave} />
      )}
      {deleteId && (
        <ConfirmDeleteModal
          title="Remove Square Footage Type"
          descr={`Are you sure you want to remove the "${deletedType?.name}" square footage type?`}
          onClose={() => setDeleteId(null)}
          onDelete={handleRemove}
          applyLabel="Remove"
        />
      )}
    </>
  );
}
